import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Checkbox, List, ListItem, ListItemButton, ListItemIcon, ListItemText, TextField, Popper, useTheme, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import i18next from 'i18next';
import Button from '@/components/UI/Button/Button';
import { LANGUAGES_ABBREVIATION, statusCampaign, statusSubCampaign, VANS_LABEL } from '@/helpers/constants';
import { toLanguageObject } from '@/helpers/utils';
import useToastContext from '@/hooks/useToastContext';
import styles from './StatusFilterDropdown.module.scss';

type LangOption = {
  DE: string;
  EN: string;
  key: string;
  type: string[];
};

type LangOptionSave = Omit<LangOption, 'DE' | 'EN'>;

type StatusFilterDropdownComponent = {
  id: string;
  onFilter: (filter: { value: ({ key: string; type: string[] })[] } | null) => void;
  filter: { value: LangOptionSave[] } | null;
  statuses: string[];
  classes?: string;
};

// fix https://github.com/mui/material-ui/issues/29207
const GroupHeader = styled('div')<{ ownerState: { backgroundColor: string; color: string } }>(({ ownerState }: any) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  textAlign: 'center',
  backgroundColor: ownerState.backgroundColor,
  color: ownerState.color,
  fontSize: '1.13rem',
  fontWeight: 'bold',
  zIndex: 10,
  boxShadow: '2px 5px 18px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
}));

const campaignStatusWithVANSLabel = [statusCampaign.IN_VAN_PLANT_IMPLEMENTATION, statusCampaign.IN_VAN_CAUSER_APPROVAL, statusCampaign.IN_VAN_PLANT_VALIDATION, statusSubCampaign.IN_VAN_PLANT_COORDINATION];

const addVansLabel = (status: string) => (campaignStatusWithVANSLabel.includes(status as any) ? VANS_LABEL : '');

const CustomPopper = (props) => <Popper {...props} style={{ width: 'fit-content' }} placement="top" />;

const initFilterValue = (filterValue: any, options) => {
  if (!filterValue || options.length === 0) return [];
  if (filterValue.length > 0 && filterValue[0]?.EN) return filterValue;
  const filterValueArray = Array.isArray(filterValue) ? filterValue : [filterValue];

  return filterValueArray.map((val: LangOptionSave) => options.find((op: LangOption) => op.key === val?.key));
};

const StatusFilterDropdown: React.FC<StatusFilterDropdownComponent> = (props) => {
  const { id, onFilter, filter, statuses, classes = '' } = props;
  const { t } = useTranslation();
  const lang = i18next.language.toUpperCase();
  const { addToast } = useToastContext();
  const theme: any = useTheme();

  const optionsAsArray = useMemo(() => {
    const result: LangOption[] = [];

    const addStatuses = (statusList: typeof statusCampaign | typeof statusSubCampaign, prefix: string, type: string) => {
      Object.values(statusList).forEach((status) => {
        if (statuses.includes(status)) {
          const langObj = Object.values(toLanguageObject(status, `${prefix}.${status}`, LANGUAGES_ABBREVIATION, addVansLabel(status)))[0] as LangOption;
          result.push({ ...langObj, type: [type] });
        }
      });
    };

    addStatuses(statusCampaign, 'campaign', 'CR');
    addStatuses(statusSubCampaign, 'subCampaign', 'SUBCR');

    return result;
  }, [statuses]);

  const [open, setOpen] = useState<boolean>(false);
  const [localValue, setLocalValue] = useState<LangOption[]>(initFilterValue(filter?.value, optionsAsArray));

  const handleClickApplyButton = (newValue: LangOption[] | undefined = undefined) => {
    const newOptions = newValue ?? localValue;
    onFilter(newOptions.length ? { value: newOptions.map((f) => ({ key: f.key, type: f.type })) } : null);
    setOpen(false);
    document.getElementById(id)?.blur();
  };

  const handleLocalChange = (ev: any, value: LangOption[], fromDeleteIcon = false) => {
    const scroll = document.getElementById(`${id}-listbox`)?.scrollTop ?? 0; // @fix reset scroll when select an option

    if (value.length > 5) {
      addToast(t('campaignRequests.maxStatusesFilter', { maxStatuses: 5 }), 'Warning', 'MAX_STATUSES_FILTER');
      setLocalValue(value.slice(0, 5));// prevents back filter
    } else {
      setLocalValue(value);
    }

    if (fromDeleteIcon) {
      handleClickApplyButton(value.slice(0, 5));
    } else {
      setTimeout(() => document.getElementById(`${id}-listbox`)?.scrollTo(0, scroll), 2);
    }
  };

  const handleOnClose = () => {
    handleLocalChange(null, initFilterValue(filter?.value, optionsAsArray));
    setOpen(false);
  };

  useEffect(() => {
    handleLocalChange(null, initFilterValue(filter?.value, optionsAsArray));
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const ListboxComponent = forwardRef<any>((propss: any, ref) => {
    const { children, ...other } = propss;

    return (
      <div ref={ref}>
        <List {...other} style={{ paddingTop: '6px' }}>
          {children}
        </List>
        <div className={styles.ApplyButton} onMouseDown={other.onMouseDown}>
          <Button buttonStyle="Primary" btnText={t('dialog.apply')} handleClick={() => handleClickApplyButton()} /> {/* dont propagate ev prop */}
        </div>
      </div>
    );
  });

  return (
    <Autocomplete
      id={id}
      multiple
      groupBy={(op: LangOption) => (op?.type[0] === 'CR' ? t('crStatuses') : t('subCrStatuses'))}
      PopperComponent={CustomPopper}
      onOpen={() => setOpen(true)}
      onClose={handleOnClose}
      open={open}
      value={localValue}
      onChange={(e, val) => handleLocalChange(e, val)}
      noOptionsText={t('noResults')}
      ListboxComponent={ListboxComponent as any}
      options={optionsAsArray}
      disableClearable
      getOptionLabel={(op) => op?.[lang]}
      isOptionEqualToValue={(option, value) => option.key === value.key}
      disableCloseOnSelect
      style={{ width: '100%' }}
      renderTags={(value, getTagProps, ownerState) => value.map((option, index) => {
        const { key, ...tagProps } = getTagProps({ index });

        return (
          <Chip
            key={key}
            label={option?.[lang] ?? ''}
            size="small"
            {...tagProps}
            onDelete={ownerState.focused
              ? (ev) => {
                ev.stopPropagation();
                handleLocalChange(null, localValue.filter((val) => val.key !== option.key), true);
              }
              : undefined}
          />
        );
      })}
      size="small"
      limitTags={1}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t('dialog.filter')}
          variant="standard"
          size="small"
          className={`${styles.StatusFilter} ${classes}`}
          onKeyDown={(event: any) => {
            if (event.key === 'Backspace') {
              event.stopPropagation();
            }
          }}
        />
      )}
      renderOption={(propss: any, option, { selected }) => (
        <ListItem key={option.key} disablePadding>
          <ListItemButton role={undefined} {...propss} onClick={propss.onClick} dense>
            <ListItemIcon sx={{ minWidth: 35 }}>
              <Checkbox edge="start" checked={selected} tabIndex={-1} disableRipple size="small" />
            </ListItemIcon>
            <ListItemText primary={option[lang]} primaryTypographyProps={{ variant: 'body1', sx: { fontSize: '1rem' } }} />
          </ListItemButton>
        </ListItem>
      )}
      renderGroup={(params) => (
        <li key={params.key}>
          <GroupHeader ownerState={{ backgroundColor: theme.cartTheme.commonGrey, color: theme.cartTheme.darkGrey }}>
            {params.group}
          </GroupHeader>
          <ul style={{ padding: 0 }}>{params.children}</ul>
        </li>
      )}
    />
  );
};

export default StatusFilterDropdown;
